import { useEffect, useRef, useState } from 'react'
import { STAGE_DESIGN_WIDTH, STAGE_REM } from '../lib/stage'
import { formatCopySnippet, useHubToolEvents } from '../lib/hubTools'
import { IS_OWNER } from '../lib/ownerMode'
import HubToolWindow from './HubToolWindow'

/** Flat hub media layout (screen-space), in rem / design-frame vw. */
export const FLAT_MEDIA_LAYOUT_DEFAULTS = {
  leftRem: 4.75,
  topRem: 7.5,
  widthVw: 55,
}

/** Contact sheet only — independent of showreel / project video layout. */
export const CONTACT_LAYOUT_DEFAULTS = {
  leftRem: 22.5,
  topRem: 12,
  widthVw: 28,
}

export const MEDIA_STORAGE_KEY = 'hub-flat-media-layout-v2'
export const CONTACT_STORAGE_KEY = 'hub-flat-contact-layout-v2'

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const StepButton = ({ label, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className="flex h-8 w-8 items-center justify-center rounded-md border border-white/20 bg-black/70 text-sm leading-none text-white transition hover:border-white/50 hover:bg-black/90 focus-visible:ring-2 focus-visible:ring-white/60"
  >
    {children}
  </button>
)

const SliderRow = ({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format = (v) => String(v),
  nudge = null,
}) => (
  <label className="block space-y-1.5">
    <div className="flex items-center justify-between gap-2 text-[11px] uppercase tracking-wide text-white/70">
      <span>{label}</span>
      <span className="font-mono text-white/90">{format(value)}</span>
    </div>
    <div className="flex items-center gap-2">
      {nudge ? (
        <StepButton
          label={`Decrease ${label}`}
          onClick={() => onChange(clamp(value - nudge, min, max))}
        >
          −
        </StepButton>
      ) : null}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="min-w-0 flex-1 accent-emerald-400"
        aria-label={label}
      />
      {nudge ? (
        <StepButton
          label={`Increase ${label}`}
          onClick={() => onChange(clamp(value + nudge, min, max))}
        >
          +
        </StepButton>
      ) : null}
    </div>
  </label>
)

const applyLayoutVars = (values, prefix) => {
  const root = document.documentElement
  root.style.setProperty(`--${prefix}-left`, `${values.leftRem * STAGE_REM}px`)
  root.style.setProperty(`--${prefix}-top`, `${values.topRem * STAGE_REM}px`)
  root.style.setProperty(
    `--${prefix}-width`,
    `${(values.widthVw / 100) * STAGE_DESIGN_WIDTH}px`
  )
}

export const applyFlatMediaLayoutVars = (values) =>
  applyLayoutVars(values, 'hub-media')

export const applyContactLayoutVars = (values) =>
  applyLayoutVars(values, 'hub-contact')

const loadLayout = (storageKey, defaults) => {
  if (!IS_OWNER) return { ...defaults }
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return { ...defaults }
    const parsed = JSON.parse(raw)
    return { ...defaults, ...parsed }
  } catch {
    return { ...defaults }
  }
}

export const loadFlatMediaLayout = (defaults = FLAT_MEDIA_LAYOUT_DEFAULTS) =>
  loadLayout(MEDIA_STORAGE_KEY, defaults)

export const loadContactLayout = (defaults = CONTACT_LAYOUT_DEFAULTS) =>
  loadLayout(CONTACT_STORAGE_KEY, defaults)

const writeLayout = (key, values) => {
  window.localStorage.setItem(key, JSON.stringify(values))
}

const PanelTransformTuner = ({
  tuneRef,
  defaults = FLAT_MEDIA_LAYOUT_DEFAULTS,
  label = 'Media layout',
  defaultOpen = false,
  columnIndex = 0,
  storageKey = MEDIA_STORAGE_KEY,
  toolId = 'media-layout',
  applyVars = applyFlatMediaLayoutVars,
  leftMin = 0,
  leftMax = 12,
  topMax = 40,
  widthMin = 28,
  widthMax = 150,
  showOwnerControls = false,
}) => {
  const [values, setValues] = useState(() => loadLayout(storageKey, defaults))
  const [saved, setSaved] = useState(false)
  const [copied, setCopied] = useState(false)
  const [saveError, setSaveError] = useState(false)
  const lastSavedRef = useRef(values)

  const commit = (next) => {
    const merged = { ...values, ...next }
    setValues(merged)
    if (tuneRef?.current) Object.assign(tuneRef.current, merged)
    applyVars(merged)
  }

  useEffect(() => {
    if (!tuneRef) return undefined
    const initial = loadLayout(storageKey, defaults)
    tuneRef.current = { ...initial }
    lastSavedRef.current = { ...initial }
    setValues(initial)
    applyVars(initial)
    return undefined
  }, [tuneRef, defaults, storageKey, applyVars])

  const handleSave = () => {
    try {
      writeLayout(storageKey, values)
      if (!window.localStorage.getItem(storageKey)) throw new Error('storage empty')
      if (tuneRef?.current) Object.assign(tuneRef.current, values)
      lastSavedRef.current = { ...values }
      applyVars(values)
      setSaveError(false)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2000)
    } catch {
      setSaveError(true)
      window.setTimeout(() => setSaveError(false), 4000)
    }
  }

  const handleCopy = async () => {
    const isContact = storageKey === CONTACT_STORAGE_KEY
    const snippet = formatCopySnippet({
      tool: 'Layout',
      profile: isContact ? 'Contact' : 'Media',
      target: isContact ? 'CONTACT_LAYOUT_DEFAULTS' : 'FLAT_MEDIA_LAYOUT_DEFAULTS',
      values,
    })
    try {
      await navigator.clipboard.writeText(snippet)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      console.log('Media layout tune:\n', snippet)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleReset = () => {
    commit({ ...lastSavedRef.current })
  }

  const handleLoadDefaults = () => {
    try {
      window.localStorage.removeItem(storageKey)
    } catch {
      // Ignore
    }
    const initial = { ...defaults }
    tuneRef.current = { ...initial }
    lastSavedRef.current = { ...initial }
    setValues(initial)
    applyVars(initial)
  }

  useHubToolEvents(toolId, {
    save: handleSave,
    reset: handleReset,
    copy: handleCopy,
    loadDefaults: handleLoadDefaults,
  })

  return (
    <HubToolWindow
      toolId={toolId}
      label={label}
      titleClassName="text-emerald-300"
      borderClassName="border-emerald-400/40"
      defaultOpen={defaultOpen}
      columnIndex={columnIndex}
    >
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3">
        <SliderRow
          label="Left (rem)"
          value={values.leftRem}
          min={leftMin}
          max={leftMax}
          step={0.25}
          nudge={0.5}
          format={(v) => v.toFixed(2)}
          onChange={(leftRem) => commit({ leftRem })}
        />
        <SliderRow
          label="Top (rem)"
          value={values.topRem}
          min={0}
          max={topMax}
          step={0.25}
          nudge={0.5}
          format={(v) => v.toFixed(2)}
          onChange={(topRem) => commit({ topRem })}
        />
        <SliderRow
          label="Width (vw)"
          value={values.widthVw}
          min={widthMin}
          max={widthMax}
          step={1}
          nudge={2}
          onChange={(widthVw) => commit({ widthVw })}
        />

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={handleReset}
            className="flex-1 rounded-md border border-white/20 px-2 py-2 text-[11px] uppercase tracking-wide text-white/80 hover:bg-white/10"
          >
            Reset
          </button>
          {showOwnerControls ? (
            <button
              type="button"
              onClick={handleCopy}
              className="flex-1 rounded-md border border-white/20 px-2 py-2 text-[11px] uppercase tracking-wide text-white/80 hover:bg-white/10"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleSave}
            className="flex-1 rounded-md border border-emerald-400/40 px-2 py-2 text-[11px] uppercase tracking-wide text-emerald-200 hover:bg-emerald-400/10"
          >
            {saved ? 'Saved' : 'Save'}
          </button>
        </div>
        <p className="text-[10px] leading-relaxed text-white/50">
          {saveError
            ? 'Edge blocked storage. Use Copy, or allow site data for localhost.'
            : 'Reset restores the last save. Save is what this browser keeps after refresh.'}
        </p>
      </div>
    </HubToolWindow>
  )
}

export default PanelTransformTuner
